import { useState, useEffect, useCallback } from "react";
import { CreditCard, Plus, Search, Filter, Trash2, X, Edit2, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import AppShell from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

var METHOD_LABELS = { boleto: "Boleto", cartao_credito: "Cartao de Credito", debito_automatico: "Debito Automatico" };
var PRODUCT_LABELS = { consorcio: "Consorcio", seguro: "Seguros", plano_saude: "Plano de Saude" };
var STATUS_LABELS = { pendente: "Pendente", pago: "Pago", vencido: "Vencido", cancelado: "Cancelado" };
var STATUS_COLORS = { pendente: "bg-amber-100 text-amber-700", pago: "bg-emerald-100 text-emerald-700", vencido: "bg-red-100 text-red-700", cancelado: "bg-gray-100 text-gray-500" };

function fmtValue(v) { return "R$ " + (v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."); }
function fmtDate(d) { if (!d) return "-"; try { var p = d.split("-"); return p[2] + "/" + p[1] + "/" + p[0]; } catch(e) { return d; } }

function daysUntil(dateStr) {
  try {
    var today = new Date(); today.setHours(0,0,0,0);
    var due = new Date(dateStr + "T00:00:00"); due.setHours(0,0,0,0);
    return Math.ceil((due - today) / 86400000);
  } catch(e) { return 999; }
}

export default function PaymentsPage() {
  var auth = useAuth();
  var isAdmin = auth.user ? auth.user.role === "admin" : false;

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState({ status: "", product: "" });
  const [searchTerm, setSearchTerm] = useState("");

  var fetchPayments = useCallback(async function() {
    setLoading(true);
    try {
      var params = [];
      if (filter.status) params.push("status=" + filter.status);
      if (filter.product) params.push("product_type=" + filter.product);
      var qs = params.length ? "?" + params.join("&") : "";
      var res = await api.get("/payments" + qs);
      setPayments(res.data.payments || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter.status, filter.product]);

  useEffect(function() { fetchPayments(); }, [fetchPayments]);

  async function handleDelete(id) {
    try { await api.delete("/payments/" + id); fetchPayments(); } catch(e) { console.error(e); }
  }
  async function handleStatusChange(id, status) {
    try { await api.put("/payments/" + id, { status: status }); fetchPayments(); } catch(e) { console.error(e); }
  }
  function handleCreated() { setShowCreate(false); fetchPayments(); }

  var filtered = payments.filter(function(p) {
    if (!searchTerm) return true;
    var s = searchTerm.toLowerCase();
    return (p.client_name || "").toLowerCase().includes(s) || (p.description || "").toLowerCase().includes(s);
  });

  // Stats
  var totalPending = 0, totalPendingVal = 0, totalOverdue = 0, totalPaid = 0;
  payments.forEach(function(p) {
    if (p.status === "pendente") { totalPending++; totalPendingVal += p.value; var d = daysUntil(p.due_date); if (d < 0) totalOverdue++; }
    if (p.status === "pago") totalPaid++;
  });

  return (
    <AppShell activePage="pagamentos" title="Pagamentos">
      <div data-testid="payments-page">
        {showCreate && <CreatePaymentModal onClose={function() { setShowCreate(false); }} onSave={handleCreated} />}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-amber-500/10 rounded-xl"><Clock className="w-4 h-4 text-amber-500" /></div><span className="text-xs text-gray-500">Pendentes</span></div>
            <p className="text-2xl font-bold text-[#0A2F3D]" data-testid="pay-kpi-pending">{totalPending}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-red-500/10 rounded-xl"><AlertTriangle className="w-4 h-4 text-red-500" /></div><span className="text-xs text-gray-500">Vencidos</span></div>
            <p className="text-2xl font-bold text-red-600" data-testid="pay-kpi-overdue">{totalOverdue}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-emerald-500/10 rounded-xl"><CheckCircle className="w-4 h-4 text-emerald-500" /></div><span className="text-xs text-gray-500">Pagos</span></div>
            <p className="text-2xl font-bold text-emerald-600" data-testid="pay-kpi-paid">{totalPaid}</p>
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><div className="p-2 bg-[#3B82F6]/10 rounded-xl"><CreditCard className="w-4 h-4 text-[#3B82F6]" /></div><span className="text-xs text-gray-500">Valor Pendente</span></div>
            <p className="text-xl font-bold text-[#0A2F3D]" data-testid="pay-kpi-value">{fmtValue(totalPendingVal)}</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
              placeholder="Buscar por cliente ou descricao..." data-testid="pay-search" />
          </div>
          <select value={filter.status} onChange={function(e) { setFilter(function(f) { return {...f, status: e.target.value}; }); }}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white" data-testid="pay-filter-status">
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="pago">Pago</option>
            <option value="vencido">Vencido</option>
          </select>
          <select value={filter.product} onChange={function(e) { setFilter(function(f) { return {...f, product: e.target.value}; }); }}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white" data-testid="pay-filter-product">
            <option value="">Todos os produtos</option>
            <option value="consorcio">Consorcio</option>
            <option value="seguro">Seguros</option>
            <option value="plano_saude">Plano de Saude</option>
          </select>
          <button onClick={function() { setShowCreate(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] whitespace-nowrap"
            data-testid="pay-add-button">
            <Plus className="w-4 h-4" /> Novo Pagamento
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm text-center py-12"><CreditCard className="w-10 h-10 text-gray-200 mx-auto mb-2" /><p className="text-sm text-gray-400">Nenhum pagamento encontrado</p></div>
        ) : (
          <div className="space-y-3" data-testid="payments-list">
            {filtered.map(function(p) {
              var days = daysUntil(p.due_date);
              var isOverdue = days < 0 && p.status === "pendente";
              var isUrgent = days >= 0 && days <= 3 && p.status === "pendente";
              var sCls = isOverdue ? "bg-red-100 text-red-700" : (STATUS_COLORS[p.status] || "bg-gray-100 text-gray-500");
              var sLabel = isOverdue ? "Vencido" : (STATUS_LABELS[p.status] || p.status);
              return (
                <div key={p.id} className={"bg-white rounded-xl shadow-sm p-4 sm:p-5 border-l-4 " + (isOverdue ? "border-l-red-500" : isUrgent ? "border-l-amber-500" : "border-l-transparent")} data-testid={"payment-card-" + p.id}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-[#0A2F3D]">{p.client_name}</p>
                      <p className="text-xs text-gray-400">{p.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={"px-2 py-1 text-xs font-medium rounded-full " + sCls}>{sLabel}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="font-semibold text-base text-[#0A2F3D]">{fmtValue(p.value)}</span>
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full">{METHOD_LABELS[p.payment_method] || p.payment_method}</span>
                    <span className="px-2 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded-full">{PRODUCT_LABELS[p.product_type] || p.product_type}</span>
                    <span>Vencimento: <strong>{fmtDate(p.due_date)}</strong></span>
                    {p.status === "pendente" && (
                      <span className={days < 0 ? "text-red-600 font-medium" : days <= 3 ? "text-amber-600 font-medium" : ""}>
                        {days < 0 ? Math.abs(days) + " dia" + (Math.abs(days) !== 1 ? "s" : "") + " atrasado" : days === 0 ? "Vence hoje!" : days + " dia" + (days !== 1 ? "s" : "") + " restante" + (days !== 1 ? "s" : "")}
                      </span>
                    )}
                    {p.reminder_sent && <span className="text-emerald-600">Lembrete enviado</span>}
                  </div>
                  {p.status === "pendente" && (
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button onClick={function() { handleStatusChange(p.id, "pago"); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100" data-testid={"pay-paid-" + p.id}>
                        <CheckCircle className="w-3 h-3" /> Marcar Pago
                      </button>
                      {isAdmin && (
                        <button onClick={function() { handleDelete(p.id); }}
                          className="ml-auto p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={"pay-delete-" + p.id}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function CreatePaymentModal({ onClose, onSave }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", client_id: "", description: "", value: "", due_date: "", payment_method: "boleto", product_type: "consorcio" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leads, setLeads] = useState([]);

  useEffect(function() {
    api.get("/leads?limit=200").then(function(r) { setLeads(r.data.leads || []); }).catch(function() {});
  }, []);

  function update(k, v) { setForm(function(f) { return {...f, [k]: v}; }); }

  function handleLeadSelect(e) {
    var lid = e.target.value;
    if (!lid) return;
    var lead = leads.find(function(l) { return l.id === lid; });
    if (lead) { setForm(function(f) { return {...f, client_id: lead.id, client_name: lead.name, client_email: lead.email || ""}; }); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.client_name || !form.value || !form.due_date || !form.description) return;
    setLoading(true); setError(null);
    try {
      await api.post("/payments", { ...form, value: parseFloat(form.value) });
      onSave();
    } catch(err) {
      var d = err.response?.data?.detail || "Erro ao criar pagamento";
      setError(typeof d === "string" ? d : "Erro");
    } finally { setLoading(false); }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md" data-testid="create-payment-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-[#0A2F3D]">Novo Pagamento</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          {error && <div className="mx-5 mt-3 px-4 py-2.5 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">{error}</div>}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {leads.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Selecionar Lead/Cliente</label>
                <select onChange={handleLeadSelect} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30" data-testid="pay-form-lead-select">
                  <option value="">Selecionar ou digitar manualmente</option>
                  {leads.map(function(l) { return <option key={l.id} value={l.id}>{l.name} {l.email ? "(" + l.email + ")" : ""}</option>; })}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Nome do Cliente *</label>
              <input type="text" value={form.client_name} onChange={function(e) { update("client_name", e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                required data-testid="pay-form-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">E-mail do Cliente</label>
              <input type="email" value={form.client_email} onChange={function(e) { update("client_email", e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                data-testid="pay-form-email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Descricao *</label>
              <input type="text" value={form.description} onChange={function(e) { update("description", e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                placeholder="Ex: Consorcio Imovel - Parcela 5/120" required data-testid="pay-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Valor (R$) *</label>
                <input type="number" step="0.01" min="0.01" value={form.value} onChange={function(e) { update("value", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                  required data-testid="pay-form-value" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Vencimento *</label>
                <input type="date" value={form.due_date} onChange={function(e) { update("due_date", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                  required data-testid="pay-form-due-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Forma de Pagamento</label>
                <select value={form.payment_method} onChange={function(e) { update("payment_method", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                  data-testid="pay-form-method">
                  <option value="boleto">Boleto</option>
                  <option value="cartao_credito">Cartao de Credito</option>
                  <option value="debito_automatico">Debito Automatico</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Produto</label>
                <select value={form.product_type} onChange={function(e) { update("product_type", e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                  data-testid="pay-form-product">
                  <option value="consorcio">Consorcio</option>
                  <option value="seguro">Seguros</option>
                  <option value="plano_saude">Plano de Saude</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={loading}
                className="flex-1 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50"
                data-testid="pay-form-submit">
                {loading ? "Criando..." : "Criar Pagamento"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
